import React, { useState, useEffect } from 'react';
import { ArrowUpRight, CheckCircle2, Linkedin, Instagram, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { track } from '../lib/analytics';

const bullets = [
  'One real teardown: what I would fix in an actual creator\u2019s email funnel and why.',
  'The exact automations and segmentation logic I build for paying clients, explained in plain English.',
  'Subject lines, send-time tests and deliverability fixes from campaigns I am running right now.',
];

const proof = [
  {
    first: 'Amber',
    niche: 'wellness coach',
    result: 'Open rates went from 17 to 20 percent up to 80 to 87 percent after we rebuilt her list and automations.',
  },
  {
    first: 'Vishakha',
    niche: 'design educator',
    result: 'Her birthday sale went from around 4K dollars to 10K dollars year over year with one email campaign.',
  },
  {
    first: 'Francisco',
    niche: 'fitness coach',
    result: 'Full CRM and funnel build with intake quiz, Stripe and behavior tagging.',
  },
];

const Newsletter = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [startTs, setStartTs] = useState(Date.now());
  const [status, setStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setStartTs(Date.now()); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, message: '' });
    if (honeypot) return;
    if (Date.now() - startTs < 2000) {
      setStatus({ type: 'error', message: 'Please take a moment to fill the form properly.' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          honeypot,
          elapsedMs: Date.now() - startTs,
          source: 'newsletter-page',
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not subscribe you right now.');
      }
      setStatus({
        type: 'success',
        message: 'You are in. Check your inbox for a confirmation from Akash.',
      });
      track('newsletter_signup', { location: 'newsletter_page' });
      setName('');
      setEmail('');
      setStartTs(Date.now());
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Could not subscribe you right now.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="The Growth Hackeng Letter · Weekly Email System Notes"
        description="Every week, one email system you can steal to turn your list into predictable revenue. For coaches, course creators and solopreneurs."
        canonical="/newsletter"
      />
      <main className="bg-cream pt-28" data-testid="newsletter-page">
        {/* Hero */}
        <section className="container-x section pt-12" data-testid="newsletter-hero">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-2 border border-line text-xs font-semibold uppercase tracking-[0.2em] text-ink">
                Newsletter
              </span>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold mt-6 leading-[0.98] text-ink tracking-tighter">
                The Growth Hackeng{' '}
                <span className="font-serif italic text-[var(--gh-orange)]">Letter</span>
              </h1>
              <p className="mt-6 text-xl md:text-2xl text-ink leading-snug font-medium">
                Every week, one email system you can steal to turn your list into predictable revenue.
              </p>
              <p className="mt-4 text-base text-muted-warm leading-relaxed max-w-xl">
                For coaches, course creators and solopreneurs who have an audience but whose email list is not making them money yet.
              </p>

              {/* You'll get */}
              <ul className="mt-10 space-y-4 max-w-xl">
                {bullets.map((b, i) => (
                  <li
                    key={b}
                    data-testid={`newsletter-bullet-${i + 1}`}
                    className="flex items-start gap-3 text-[15px] text-ink leading-relaxed"
                  >
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Form card */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="rounded-[1.75rem] bg-cream-2 border border-line p-8 md:p-10">
                <p className="text-xs uppercase tracking-[0.25em] text-[var(--gh-orange)] font-semibold">
                  Subscribe free
                </p>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold mt-3 text-ink leading-tight">
                  One useful email, every Friday.
                </h2>
                <p className="mt-3 text-sm text-muted-warm">
                  No fluff, no re-shared LinkedIn takes. Just what I am building for clients that week.
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="newsletter-page-form">
                  <div>
                    <label className="block text-xs uppercase tracking-widest font-semibold text-muted-warm mb-2">
                      First name
                    </label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Akash"
                      data-testid="newsletter-name-input"
                      className="w-full px-4 py-3 rounded-2xl bg-cream border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest font-semibold text-muted-warm mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@brand.com"
                      data-testid="newsletter-page-email-input"
                      className="w-full px-4 py-3 rounded-2xl bg-cream border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
                    />
                  </div>

                  {/* honeypot */}
                  <input
                    type="text"
                    name="company"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                  />

                  {status.type === 'error' && (
                    <p className="text-sm text-red-600" data-testid="newsletter-page-error">{status.message}</p>
                  )}
                  {status.type === 'success' && (
                    <p className="text-sm text-[var(--gh-orange)] font-semibold" data-testid="newsletter-page-success">
                      {status.message}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="newsletter-page-submit"
                    className="btn-orange w-full justify-center disabled:opacity-60"
                  >
                    {submitting ? 'Adding you…' : 'Subscribe'}
                    {!submitting && <ArrowUpRight className="w-4 h-4" />}
                  </button>
                  <p className="text-[11px] text-muted-warm text-center">
                    Unsubscribe any Friday with one click.
                  </p>
                </form>

                {/* Social handoff */}
                <div className="mt-8 pt-6 border-t border-line">
                  <p className="text-sm text-ink leading-relaxed">
                    New here? I share email breakdowns and systems daily on LinkedIn and Instagram.
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <a
                      href="https://www.linkedin.com/in/akashtiwarii/"
                      target="_blank" rel="noopener noreferrer"
                      data-testid="newsletter-linkedin"
                      aria-label="LinkedIn"
                      className="w-10 h-10 rounded-full border border-line hover:border-[var(--gh-orange)] hover:text-[var(--gh-orange)] flex items-center justify-center transition-colors text-ink"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                    <a
                      href="https://www.instagram.com/growthhackeng/"
                      target="_blank" rel="noopener noreferrer"
                      data-testid="newsletter-instagram"
                      aria-label="Instagram"
                      className="w-10 h-10 rounded-full border border-line hover:border-[var(--gh-orange)] hover:text-[var(--gh-orange)] flex items-center justify-center transition-colors text-ink"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                    <a
                      href="/contact"
                      data-testid="newsletter-contact"
                      aria-label="Contact"
                      className="w-10 h-10 rounded-full border border-line hover:border-[var(--gh-orange)] hover:text-[var(--gh-orange)] flex items-center justify-center transition-colors text-ink"
                    >
                      <Mail className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Understated proof strip */}
        <section className="container-x section pt-0" data-testid="newsletter-proof">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] text-[var(--gh-orange)] font-semibold">
              What the systems produce
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-3 text-ink leading-[1.1]">
              A few real client outcomes.
            </h2>
            <p className="mt-3 text-base text-muted-warm">
              These are the kinds of teardowns you will read about inside the letter.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {proof.map((p, i) => (
              <div
                key={p.first}
                data-testid={`newsletter-proof-${i + 1}`}
                className="rounded-2xl bg-cream border border-line p-6"
              >
                <p className="text-xs uppercase tracking-widest text-muted-warm font-semibold">
                  {p.first}, {p.niche}
                </p>
                <p className="mt-3 text-[15px] text-ink leading-relaxed">
                  {p.result}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Closing bar */}
        <section className="container-x section pt-0" data-testid="newsletter-closing">
          <div className="rounded-[1.75rem] bg-ink text-[var(--gh-cream)] p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[var(--gh-orange)] font-semibold">
                Ready
              </p>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold mt-2 leading-tight">
                One Friday email that pays the rent on your list.
              </h3>
            </div>
            <a
              href="#top"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              data-testid="newsletter-closing-cta"
              className="btn-orange"
            >
              Subscribe now <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Newsletter;
